// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Losing the GPU, and getting it back (M3-01).
//
// A WebGL context is not owned by the page. The browser takes it away when a phone rotates,
// when a tab is backgrounded long enough, when another tab is hostile, when the driver resets,
// and on Quest whenever the headset sleeps. It is routine, and it is the one failure mode that
// makes a web game look broken in a way a native one does not: the canvas goes black and stays
// black for ever.
//
// Two rules, both of which are easy to get wrong and neither of which is obvious:
//
//  1. **`preventDefault()` on `webglcontextlost`, or `webglcontextrestored` never fires.** The
//     default action is "this context is gone for good". Miss this line and every recovery
//     path below is dead code that has never once run.
//  2. **The simulation keeps going.** The GPU is a display device. A machine whose ball stops
//     mid-flight because a driver reset is a machine that lost the player's game to something
//     that had nothing to do with them. The pictures stop; the game does not.
//
// Every GL resource — textures, geometry, programs, framebuffers — is invalid after a loss and
// has to be rebuilt, so the host hands this machine the one callback that knows how to build
// the scene, and it is called again on restore.

export type ContextState = 'live' | 'lost' | 'rebuilding'

export interface ContextLossReport {
  readonly state: ContextState
  /** How many times the context has been lost this session. A diagnostic, surfaced not hidden. */
  readonly losses: number
  /** How many of those were recovered from. `losses - recoveries` is what is still broken. */
  readonly recoveries: number
}

/**
 * The loss/restore state machine, with no WebGL in it.
 *
 * Separated so the sequence can be tested — including the sequences that never happen on a
 * healthy machine and always happen on a bad one: a second loss during the rebuild, a restore
 * that arrives without a preceding loss, a loss while already lost.
 */
export class ContextLossTracker {
  private current: ContextState = 'live'
  private lossCount = 0
  private recoveryCount = 0

  get state(): ContextState {
    return this.current
  }

  /** True while the renderer must not touch the GPU. The simulation ignores this entirely. */
  get renderable(): boolean {
    return this.current === 'live'
  }

  report(): ContextLossReport {
    return { state: this.current, losses: this.lossCount, recoveries: this.recoveryCount }
  }

  /** The context went away. Idempotent: a second loss while already lost is not a second loss. */
  onLost(): void {
    if (this.current === 'lost') return
    this.current = 'lost'
    this.lossCount++
  }

  /**
   * The browser says we may have a context again.
   *
   * Answers whether the caller should rebuild. A restore with no loss behind it is ignored
   * rather than trusted — it would mean rebuilding a scene that is already live, throwing away
   * perfectly good resources for nothing.
   */
  onRestored(): boolean {
    if (this.current !== 'lost') return false
    this.current = 'rebuilding'
    return true
  }

  /** The rebuild finished. Only counts as a recovery if a rebuild was actually in progress. */
  onRebuilt(): void {
    if (this.current !== 'rebuilding') return
    this.current = 'live'
    this.recoveryCount++
  }

  /**
   * The rebuild failed, or the context was lost again during it.
   *
   * Back to `lost` without a recovery, and without a second loss on the tally: the context has
   * been gone since the first one, and counting the retry would report two outages where the
   * player experienced one.
   */
  onRebuildFailed(): void {
    if (this.current !== 'rebuilding') return
    this.current = 'lost'
  }
}
